import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import { isChairmanReadOnly } from '../utils/access';

const API         = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/secretariat';
const BIO_API     = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/member-bio';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';
const TREASURY_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury';

const MEETING_TYPES  = ['AGM', 'EGM', 'Executive', 'General', 'Emergency', 'Special'];
const COMMITTEES     = ['Executive_Committee', 'Finance_Committee', 'Grievance_Committee'];
const IMPL_STATUSES  = ['pending', 'in_progress', 'completed', 'overdue', 'deferred'];
const RELATIONSHIPS  = ['Sibling', 'Parent', 'Child', 'Spouse', 'Uncle', 'Aunt', 'Nephew', 'Niece', 'Cousin', 'Friend', 'Colleague', 'Other'];
const MEMORIAL_RELATIONSHIPS = ['Father', 'Mother', 'Spouse', 'Child', 'Member'];
const MEMORIAL_STATUSES      = ['reported', 'approved', 'paid', 'closed'];
const MEMORIAL_STATUS_COLORS = { reported: '#f39c12', approved: '#2980b9', paid: '#27ae60', closed: '#95a5a6' };

function Secretariat() {
  const [activeTab,      setActiveTab]      = useState('meetings');
  const [leadershipView, setLeadershipView] = useState('active'); // 'active' | 'retired'
  const [meetings,       setMeetings]       = useState([]);
  const [resolutions,    setResolutions]    = useState([]);
  const [leadership,     setLeadership]     = useState([]);
  const [correspondence, setCorrespondence] = useState([]);
  const [members,        setMembers]        = useState([]);
  const [showForm,       setShowForm]       = useState(false);
  const [editingId,      setEditingId]      = useState(null);
  const [editData,       setEditData]       = useState({});
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');
  const [minutes,        setMinutes]        = useState([]);
  const [uploadForm,     setUploadForm]     = useState({ meeting_id: '', notes: '' });
  const [uploading,      setUploading]      = useState(false);
  const [selectedFile,   setSelectedFile]   = useState(null);
  const [resolutionFile, setResolutionFile] = useState(null);
  const [corrFile,       setCorrFile]       = useState(null);

  // BIO state
  const [bioMemberId, setBioMemberId] = useState('');
  const [bioData,     setBioData]     = useState(null);
  const [bioChildren, setBioChildren] = useState([]);
  const [bioSpouses,  setBioSpouses]  = useState([]);
  const [bioLoading,  setBioLoading]  = useState(false);
  const [bioForm,     setBioForm]     = useState({
    father_name: '', father_deceased: false,
    mother_name: '', mother_deceased: false,
    next_of_kin_name: '', next_of_kin_relationship: '',
    lc1: '', lc2: '', lc3: '', district: ''
  });
  const [newChildName, setNewChildName] = useState('');
  const [newSpouseName, setNewSpouseName] = useState('');
  const [bioSaving,    setBioSaving]    = useState(false);
  const [deleteBioModal, setDeleteBioModal] = useState(false);
  const [deleteBioConfirmText, setDeleteBioConfirmText] = useState('');
  const [deletingBio, setDeletingBio] = useState(false);
  const [editingChildId, setEditingChildId] = useState(null);
  const [editingChildName, setEditingChildName] = useState('');
  const [editingSpouseId, setEditingSpouseId] = useState(null);
  const [editingSpouseName, setEditingSpouseName] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [leadershipPdfGenerating, setLeadershipPdfGenerating] = useState(false);

  // Memorial form: the selected member's BIO (parents, spouses, children)
  // feeds the Name of Deceased dropdown — no typing.
  const [memBio, setMemBio] = useState({ father: '', mother: '', spouses: [], children: [] });
  const [memBioLoading, setMemBioLoading] = useState(false);

  // Memorial state
  const [memorial,     setMemorial]     = useState([]);
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const [disburseModal, setDisburseModal] = useState(null); // { record } | null
  const [disburseAccountId, setDisburseAccountId] = useState('');
  const [disbursing, setDisbursing] = useState(false);
  const [memorialForm, setMemorialForm] = useState({
    member_id: '', deceased_name: '', relationship: 'Father',
    date_of_death: '', package_amount: '', status: 'reported', notes: ''
  });

  const [meetingForm, setMeetingForm] = useState({
    meeting_type: 'AGM', title: '', date: '',
    time_start: '', time_end: '', venue: '', quorum_required: '',
    agenda: '', chaired_by: '', minuted_by: ''
  });
  const [resolutionForm, setResolutionForm] = useState({
    resolution_number: '', meeting_id: '', title: '', motion: '',
    proposed_by: '', seconded_by: '', votes_for: '', votes_against: '',
    votes_abstained: '', voting_threshold: 'simple_majority', outcome: 'passed',
    implementation_deadline: '', financial_implication: false, amount_approved: ''
  });
  const [leadershipForm, setLeadershipForm] = useState({
    member_id: '', position: '', committee: 'Executive_Committee',
    election_date: '', term_start: '', term_end: '', term_duration_years: ''
  });
  const [corrForm, setCorrForm] = useState({
    ref_number: '', direction: 'IN', correspondent: '', subject: '',
    summary: '', date: new Date().toISOString().split('T')[0],
    category: 'general', requires_action: false, action_deadline: ''
  });

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const readOnly = isChairmanReadOnly();

  useEffect(() => {
    fetchAll();
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
    axios.get(`${TREASURY_API}/accounts`, { headers }).then(r => setTreasuryAccounts(r.data)).catch(() => {});
  }, []);

  const fetchAll = () => {
    axios.get(`${API}/meetings`,       { headers }).then(r => setMeetings(r.data)).catch(() => {});
    axios.get(`${API}/resolutions`,    { headers }).then(r => setResolutions(r.data)).catch(() => {});
    axios.get(`${API}/leadership`,     { headers }).then(r => setLeadership(r.data)).catch(() => {});
    axios.get(`${API}/correspondence`, { headers }).then(r => setCorrespondence(r.data)).catch(() => {});
    axios.get(`${API}/minutes`,        { headers }).then(r => setMinutes(r.data)).catch(() => {});
    axios.get(`${API}/bereavement`,    { headers }).then(r => setMemorial(r.data)).catch(() => {});
  };

  const showMsg = (text, type = 'success') => {
    if (type === 'error') setError(text); else setSuccess(text);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  // BIO handlers
  const loadBio = async (memberId) => {
    if (!memberId) { setBioData(null); setBioChildren([]); setBioSpouses([]); return; }
    setBioLoading(true);
    try {
      const res = await axios.get(`${BIO_API}/${memberId}`, { headers });
      const { bio, children, spouses } = res.data;
      setBioData(bio); setBioChildren(children || []); setBioSpouses(spouses || []);
      setBioForm(bio ? {
        father_name: bio.father_name || '', father_deceased: bio.father_deceased || false,
        mother_name: bio.mother_name || '', mother_deceased: bio.mother_deceased || false,
        next_of_kin_name: bio.next_of_kin_name || '', next_of_kin_relationship: bio.next_of_kin_relationship || '',
        lc1: bio.lc1 || '', lc2: bio.lc2 || '', lc3: bio.lc3 || '', district: bio.district || ''
      } : { father_name: '', father_deceased: false, mother_name: '', mother_deceased: false, next_of_kin_name: '', next_of_kin_relationship: '', lc1: '', lc2: '', lc3: '', district: '' });
    } catch (err) { showMsg('Failed to load BIO data', 'error'); }
    setBioLoading(false);
  };

  const saveBio = async () => {
    if (!bioMemberId) return showMsg('Select a member first', 'error');
    setBioSaving(true);
    try {
      await axios.post(`${BIO_API}/${bioMemberId}`, bioForm, { headers });
      showMsg('BIO data saved successfully'); loadBio(bioMemberId);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to save', 'error'); }
    setBioSaving(false);
  };

  // Delete entire BIO record — destructive, for correcting a mistaken
  // entry. Requires typing the member's exact name to confirm.
  const currentBioMember = members.find(m => String(m.id) === String(bioMemberId));
  const deleteEntireBio = async () => {
    if (!currentBioMember) return;
    if (deleteBioConfirmText.trim().toUpperCase() !== currentBioMember.full_name.trim().toUpperCase()) {
      return showMsg('Typed name does not match — record not deleted', 'error');
    }
    setDeletingBio(true);
    try {
      await axios.delete(`${BIO_API}/${bioMemberId}`, { headers });
      showMsg('BIO record deleted');
      setDeleteBioModal(false); setDeleteBioConfirmText('');
      loadBio(bioMemberId);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to delete', 'error'); }
    setDeletingBio(false);
  };

  const saveChildEdit = async (childId) => {
    if (!editingChildName.trim()) return showMsg('Child name cannot be empty', 'error');
    try {
      await axios.patch(`${BIO_API}/children/${childId}`, { child_name: editingChildName }, { headers });
      setEditingChildId(null); loadBio(bioMemberId);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to update', 'error'); }
  };

  const saveSpouseEdit = async (spouseId) => {
    if (!editingSpouseName.trim()) return showMsg('Spouse name cannot be empty', 'error');
    try {
      await axios.patch(`${BIO_API}/spouses/${spouseId}`, { spouse_name: editingSpouseName }, { headers });
      setEditingSpouseId(null); loadBio(bioMemberId);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to update', 'error'); }
  };

  const addChild = async () => {
    if (!newChildName.trim()) return showMsg('Enter child name', 'error');
    try {
      await axios.post(`${BIO_API}/${bioMemberId}/children`, { child_name: newChildName }, { headers });
      setNewChildName(''); loadBio(bioMemberId);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const removeChild = async (childId) => {
    if (!window.confirm('Remove this child record?')) return;
    try { await axios.delete(`${BIO_API}/children/${childId}`, { headers }); loadBio(bioMemberId); }
    catch (err) { showMsg('Failed to remove', 'error'); }
  };

  // Spouse handlers — supports multiple spouses per member
  const addSpouse = async () => {
    if (!newSpouseName.trim()) return showMsg('Enter spouse name', 'error');
    try {
      await axios.post(`${BIO_API}/${bioMemberId}/spouses`, { spouse_name: newSpouseName }, { headers });
      setNewSpouseName(''); loadBio(bioMemberId);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const removeSpouse = async (spouseId) => {
    if (!window.confirm('Remove this spouse record?')) return;
    try { await axios.delete(`${BIO_API}/spouses/${spouseId}`, { headers }); loadBio(bioMemberId); }
    catch (err) { showMsg('Failed to remove', 'error'); }
  };

  // Bio-Data PDF
  const downloadBioPDF = async () => {
    if (!bioMemberId) return;
    setPdfGenerating(true);
    try {
      const genRes = await axios.get(`${BIO_API}/${bioMemberId}/generate-pdf`, { headers });
      // Fetch the actual file via axios (carries the auth token) rather than
      // window.open() on a raw URL — a plain browser navigation sends no
      // Authorization header and gets blocked by the auth middleware on
      // this route. Fetching as a blob and opening that works regardless
      // of how the route is protected.
      const fileRes = await axios.get(`http://localhost:5000${genRes.data.download_url}`, {
        headers, responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([fileRes.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to generate PDF', 'error'); }
    setPdfGenerating(false);
  };

  // Leadership Register PDF (full registry — active + retired)
  const downloadLeadershipPDF = async () => {
    setLeadershipPdfGenerating(true);
    try {
      const genRes = await axios.get(`${API}/leadership/generate-pdf`, { headers });
      const fileRes = await axios.get(`http://localhost:5000${genRes.data.download_url}`, {
        headers, responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([fileRes.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to generate PDF', 'error'); }
    setLeadershipPdfGenerating(false);
  };

  // Memorial handlers
  // Load the selected member's BIO for the Name of Deceased dropdown.
  useEffect(() => {
    const id = memorialForm.member_id;
    if (!id) { setMemBio({ father: '', mother: '', spouses: [], children: [] }); return; }
    let cancelled = false;
    setMemBioLoading(true);
    axios.get(`${BIO_API}/${id}`, { headers })
      .then(r => {
        if (cancelled) return;
        const { bio, children, spouses } = r.data || {};
        setMemBio({
          father: bio?.father_name || '', mother: bio?.mother_name || '',
          spouses: (spouses || []).map(s => s.spouse_name).filter(Boolean),
          children: (children || []).map(c => c.child_name).filter(Boolean),
        });
      })
      .catch(() => { if (!cancelled) setMemBio({ father: '', mother: '', spouses: [], children: [] }); })
      .finally(() => { if (!cancelled) setMemBioLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [memorialForm.member_id]);

  const memorialNames = (() => {
    const rel = memorialForm.relationship;
    if (rel === 'Father')  return memBio.father ? [memBio.father] : [];
    if (rel === 'Mother')  return memBio.mother ? [memBio.mother] : [];
    if (rel === 'Spouse')  return memBio.spouses;
    if (rel === 'Child')   return memBio.children;
    if (rel === 'Member') {
      const m = members.find(x => String(x.id) === String(memorialForm.member_id));
      return m ? [m.full_name] : [];
    }
    return [];
  })();

  const isAlreadyRecorded = (name) => memorial.some(r =>
    String(r.member_id) === String(memorialForm.member_id) &&
    r.relationship === memorialForm.relationship &&
    String(r.deceased_name || '').trim().toLowerCase() === String(name).trim().toLowerCase());

  const submitMemorial = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bereavement`, {
        ...memorialForm,
        date_reported: new Date().toISOString().split('T')[0]
      }, { headers });
      showMsg('Memorial record added'); setShowForm(false);
      setMemorialForm({ member_id: '', deceased_name: '', relationship: 'Father', date_of_death: '', package_amount: '', status: 'reported', notes: '' });
      fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  // Disburse flow — opens a modal to pick which treasury account to debit,
  // since bereavement payouts aren't tied to one fixed account.
  const openDisburseModal = (record) => { setDisburseModal({ record }); setDisburseAccountId(''); };
  const cancelDisburseModal = () => { setDisburseModal(null); setDisburseAccountId(''); };

  const confirmDisburse = async () => {
    if (!disburseAccountId) return showMsg('Select a treasury account', 'error');
    setDisbursing(true);
    try {
      await axios.post(`${API}/bereavement/${disburseModal.record.id}/disburse`, { account_id: disburseAccountId }, { headers });
      showMsg('Disbursed — treasury debited and record marked paid');
      setDisburseModal(null); setDisburseAccountId('');
      fetchAll();
      axios.get(`${TREASURY_API}/accounts`, { headers }).then(r => setTreasuryAccounts(r.data)).catch(() => {});
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to disburse', 'error'); }
    setDisbursing(false);
  };

  const deleteMemorial = async (record) => {
    const warning = record.status === 'paid'
      ? `Delete this record? It was already disbursed — deleting it will REVERSE the treasury debit (credit back UGX ${parseFloat(record.package_amount || 0).toLocaleString()}).`
      : 'Delete this memorial record?';
    if (!window.confirm(warning)) return;
    try {
      await axios.delete(`${API}/bereavement/${record.id}`, { headers });
      showMsg('Record deleted'); fetchAll();
      axios.get(`${TREASURY_API}/accounts`, { headers }).then(r => setTreasuryAccounts(r.data)).catch(() => {});
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to delete', 'error'); }
  };

  // Other handlers
  const uploadMinutes = async (e) => {
    e.preventDefault();
    if (!selectedFile) return showMsg('Select a PDF file', 'error');
    if (!uploadForm.meeting_id) return showMsg('Select a meeting', 'error');
    setUploading(true);
    const formData = new FormData();
    formData.append('minutes_file', selectedFile);
    formData.append('meeting_id', uploadForm.meeting_id);
    formData.append('notes', uploadForm.notes || '');
    try {
      await axios.post(`${API}/minutes/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      showMsg('Minutes uploaded'); setShowForm(false); setUploadForm({ meeting_id: '', notes: '' }); setSelectedFile(null); fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Upload failed', 'error'); }
    finally { setUploading(false); }
  };

  const deleteMinutes = async (id) => {
    if (!window.confirm('Delete these minutes?')) return;
    try { await axios.delete(`${API}/minutes/${id}`, { headers }); showMsg('Deleted'); fetchAll(); }
    catch (err) { showMsg('Failed', 'error'); }
  };

  const deleteMeeting = async (m) => {
    if (!window.confirm(`Delete meeting ${m.meeting_number} — "${m.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/meetings/${m.id}`, { headers });
      showMsg('Meeting deleted'); fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to delete', 'error'); }
  };

  const submitMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meetings`, meetingForm, { headers });
      showMsg('Meeting scheduled — number auto-assigned'); setShowForm(false);
      setMeetingForm({ meeting_type: 'AGM', title: '', date: '', time_start: '', time_end: '', venue: '', quorum_required: '', agenda: '', chaired_by: '', minuted_by: '' });
      fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const submitResolution = async (e) => {
    e.preventDefault();
    if (!resolutionFile) return showMsg('Attach the supporting PDF document', 'error');
    const formData = new FormData();
    formData.append('document', resolutionFile);
    Object.entries(resolutionForm).forEach(([k, v]) => formData.append(k, v));
    try {
      await axios.post(`${API}/resolutions`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      showMsg('Resolution recorded'); setShowForm(false);
      setResolutionForm({ resolution_number: '', meeting_id: '', title: '', motion: '', proposed_by: '', seconded_by: '', votes_for: '', votes_against: '', votes_abstained: '', voting_threshold: 'simple_majority', outcome: 'passed', implementation_deadline: '', financial_implication: false, amount_approved: '' });
      setResolutionFile(null);
      fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const submitLeadership = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leadership`, leadershipForm, { headers });
      showMsg('Leadership record saved'); setShowForm(false);
      setLeadershipForm({ member_id: '', position: '', committee: 'Executive_Committee', election_date: '', term_start: '', term_end: '', term_duration_years: '' });
      fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  // Retire flow — one-click action distinct from the generic Edit path.
  // Sets status='retired' and auto-fills term_end to today if not already
  // set. Retired leaders are never deleted; they move into a separate
  // "Retired Leaders" view so the register stays a permanent historical
  // record of who has served.
  const retireLeader = async (l) => {
    if (!window.confirm(`Retire ${l.full_name} from ${l.position}? This moves them to the Retired Leaders list.`)) return;
    try {
      await axios.patch(`${API}/leadership/${l.id}`, {
        status: 'retired',
        term_end: l.term_end || new Date().toISOString().split('T')[0]
      }, { headers });
      showMsg(`${l.full_name} retired`); fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to retire', 'error'); }
  };

  const submitCorr = async (e) => {
    e.preventDefault();
    if (!corrFile) return showMsg('Attach the letter/document', 'error');
    const formData = new FormData();
    formData.append('document', corrFile);
    Object.entries(corrForm).forEach(([k, v]) => formData.append(k, v));
    try {
      await axios.post(`${API}/correspondence`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      showMsg('Correspondence logged'); setShowForm(false);
      setCorrForm({ ref_number: '', direction: 'IN', correspondent: '', subject: '', summary: '', date: new Date().toISOString().split('T')[0], category: 'general', requires_action: false, action_deadline: '' });
      setCorrFile(null);
      fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const saveEdit = async (endpoint, id) => {
    try {
      await axios.patch(`${API}/${endpoint}/${id}`, editData, { headers });
      showMsg('Updated'); setEditingId(null); fetchAll();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const inp       = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
  const inlineInp = { padding: '4px 6px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '100%' };
  const fld       = (w) => ({ display: 'flex', flexDirection: 'column', gap: '4px', flex: w || 1, minWidth: '150px' });
  const lbl       = { fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' };
  const btn       = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });
  const smBtn     = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' });
  const th        = { padding: '10px', textAlign: 'left', fontSize: '12px' };
  const td        = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
  const badge     = (color, text) => <span style={{ background: color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>{text}</span>;

  const statusColor  = { scheduled: '#2980b9', held: '#27ae60', cancelled: '#e74c3c', postponed: '#f39c12' };
  const outcomeColor = { passed: '#27ae60', rejected: '#e74c3c', deferred: '#f39c12', withdrawn: '#95a5a6' };
  const implColor    = { pending: '#f39c12', in_progress: '#2980b9', completed: '#27ae60', overdue: '#e74c3c', deferred: '#95a5a6' };
  const dirColor     = { IN: '#27ae60', OUT: '#2980b9' };

  const tabs = [
    ['meetings',      `Meetings (${meetings.length})`],
    ['resolutions',   `Resolutions (${resolutions.length})`],
    ['leadership',    `Leadership (${leadership.length})`],
    ['correspondence',`Correspondence (${correspondence.length})`],
    ['minutes',       `📄 Minutes (${minutes.length})`],
    ['bio',           '👤 Member BIO'],
    ['memorial',      `🕊 Memorial Register (${memorial.length})`],
  ];

  const specialTabs = { bio: '#1a5276', memorial: '#6c3483' };

  return (
    <div style={{ padding: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ color: '#2c3e50', margin: 0 }}>Secretariat</h2>
        {!readOnly && activeTab !== 'bio' && (
          <button onClick={() => setShowForm(!showForm)}
            style={btn(activeTab === 'memorial' ? '#6c3483' : '#2c3e50')}>
            {showForm ? 'Cancel' : activeTab === 'memorial' ? '+ Add Record' : `+ Add ${activeTab.slice(0, -1)}`}
          </button>
        )}
      </div>

      {/* Alerts */}
      {error   && <div style={{ background: '#e74c3c', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '12px' }}>{error}   <span onClick={() => setError('')}   style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={{ background: '#2ecc71', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '12px' }}>{success} <span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #ecf0f1', flexWrap: 'wrap' }}>
        {tabs.map(([key, label]) => {
          const color = specialTabs[key] || '#2c3e50';
          return (
            <button key={key} onClick={() => { setActiveTab(key); setShowForm(false); setEditingId(null); }} style={{
              padding: '10px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
              background: activeTab === key ? color : 'transparent',
              color: activeTab === key ? 'white' : (specialTabs[key] ? color : '#7f8c8d'),
              borderRadius: '4px 4px 0 0'
            }}>{label}</button>
          );
        })}
      </div>

      {/* ── MEETINGS ── */}
      {activeTab === 'meetings' && (
        <div>
          {!readOnly && showForm && (
            <form onSubmit={submitMeeting} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ width: '100%', margin: '0 0 8px', color: '#2c3e50' }}>Schedule Meeting</h3>
              <div style={fld()}><label style={lbl}>Type</label>
                <select style={inp} value={meetingForm.meeting_type} onChange={e => setMeetingForm({ ...meetingForm, meeting_type: e.target.value })}>
                  {MEETING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={fld()}>
                <label style={lbl}>Meeting Number</label>
                <div style={{ ...inp, background: '#eee', color: '#888', display: 'flex', alignItems: 'center' }}>Auto-assigned on save</div>
              </div>
              <div style={{ ...fld(2) }}><label style={lbl}>Title *</label><input style={inp} value={meetingForm.title} onChange={e => setMeetingForm({ ...meetingForm, title: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Date *</label><input type="date" style={inp} value={meetingForm.date} onChange={e => setMeetingForm({ ...meetingForm, date: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Time Start *</label><input type="time" style={inp} value={meetingForm.time_start} onChange={e => setMeetingForm({ ...meetingForm, time_start: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Time End</label><input type="time" style={inp} value={meetingForm.time_end} onChange={e => setMeetingForm({ ...meetingForm, time_end: e.target.value })} /></div>
              <div style={fld()}><label style={lbl}>Venue *</label><input style={inp} value={meetingForm.venue} onChange={e => setMeetingForm({ ...meetingForm, venue: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Quorum Required</label><input type="number" style={inp} value={meetingForm.quorum_required} onChange={e => setMeetingForm({ ...meetingForm, quorum_required: e.target.value })} /></div>
              <div style={fld()}><label style={lbl}>Chaired By</label>
                <select style={inp} value={meetingForm.chaired_by} onChange={e => setMeetingForm({ ...meetingForm, chaired_by: e.target.value })}>
                  <option value="">-- Select --</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                </select>
              </div>
              <div style={fld()}><label style={lbl}>Minuted By</label>
                <select style={inp} value={meetingForm.minuted_by} onChange={e => setMeetingForm({ ...meetingForm, minuted_by: e.target.value })}>
                  <option value="">-- Select --</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                </select>
              </div>
              <div style={{ width: '100%' }}><label style={lbl}>Agenda</label><textarea style={{ ...inp, height: '70px' }} value={meetingForm.agenda} onChange={e => setMeetingForm({ ...meetingForm, agenda: e.target.value })} /></div>
              <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('#2c3e50')}>Schedule Meeting</button>
                <button type="button" onClick={() => setShowForm(false)} style={btn('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
              {['Number', 'Type', 'Title', 'Date', 'Time', 'Venue', 'Quorum', 'Status', 'Chaired By', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {meetings.length === 0 && <tr><td colSpan="10" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No meetings yet</td></tr>}
              {meetings.map((m, i) => (
                editingId === `meeting-${m.id}` ? (
                  <tr key={m.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                    <td style={td}><input value={editData.meeting_number || ''} onChange={e => setEditData({ ...editData, meeting_number: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><select value={editData.meeting_type || ''} onChange={e => setEditData({ ...editData, meeting_type: e.target.value })} style={inlineInp}>{MEETING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></td>
                    <td style={td}><input value={editData.title || ''} onChange={e => setEditData({ ...editData, title: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input type="date" value={editData.date || ''} onChange={e => setEditData({ ...editData, date: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input type="time" value={editData.time_start || ''} onChange={e => setEditData({ ...editData, time_start: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input value={editData.venue || ''} onChange={e => setEditData({ ...editData, venue: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input type="number" value={editData.quorum_present || ''} onChange={e => setEditData({ ...editData, quorum_present: e.target.value })} style={inlineInp} placeholder="Present" /></td>
                    <td style={td}><select value={editData.status || ''} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInp}>{['scheduled','held','cancelled','postponed'].map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td style={td}>{m.chaired_by_name || '—'}</td>
                    <td style={td}>
                      {!readOnly && <button onClick={() => saveEdit('meetings', m.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                      <button onClick={cancelEdit} style={smBtn('#95a5a6')}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={td}>{m.meeting_number}</td>
                    <td style={{ ...td, fontSize: '11px' }}>{m.meeting_type}</td>
                    <td style={td}>{m.title}</td>
                    <td style={td}>{m.date?.slice(0, 10)}</td>
                    <td style={td}>{m.time_start ? m.time_start.slice(0, 5) : '—'}</td>
                    <td style={td}>{m.venue}</td>
                    <td style={td}>{m.quorum_present ? `${m.quorum_present}/${m.quorum_required}` : `—/${m.quorum_required}`}</td>
                    <td style={td}>{badge(statusColor[m.status] || '#95a5a6', m.status)}</td>
                    <td style={td}>{m.chaired_by_name || '—'}</td>
                    <td style={td}>
                      {!readOnly && <button onClick={() => { setEditingId(`meeting-${m.id}`); setEditData({ meeting_number: m.meeting_number, meeting_type: m.meeting_type, title: m.title, date: m.date?.slice(0, 10) || '', time_start: m.time_start ? m.time_start.slice(0, 5) : '', venue: m.venue, quorum_required: m.quorum_required, quorum_present: m.quorum_present || '', status: m.status }); }} style={{ ...smBtn('#e67e22'), marginRight: '4px' }}>Edit</button>}
                      {!readOnly && <button onClick={() => deleteMeeting(m)} style={{ background: '#fde8e8', color: '#c0392b', border: '1px solid #f5c6cb', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── RESOLUTIONS ── */}
      {activeTab === 'resolutions' && (
        <div>
          {!readOnly && showForm && (
            <form onSubmit={submitResolution} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ width: '100%', margin: '0 0 8px', color: '#2c3e50' }}>Record Resolution</h3>
              <div style={fld()}><label style={lbl}>Resolution Number *</label><input style={inp} value={resolutionForm.resolution_number} onChange={e => setResolutionForm({ ...resolutionForm, resolution_number: e.target.value })} placeholder="RES-AGM-2026-001" required /></div>
              <div style={fld()}><label style={lbl}>Meeting *</label>
                <select style={inp} value={resolutionForm.meeting_id} onChange={e => setResolutionForm({ ...resolutionForm, meeting_id: e.target.value })} required>
                  <option value="">-- Select Meeting --</option>
                  {meetings.map(m => <option key={m.id} value={m.id}>{m.meeting_number} — {m.date?.slice(0, 10)}</option>)}
                </select>
              </div>
              <div style={{ ...fld(2) }}><label style={lbl}>Title *</label><input style={inp} value={resolutionForm.title} onChange={e => setResolutionForm({ ...resolutionForm, title: e.target.value })} required /></div>
              <div style={{ width: '100%' }}><label style={lbl}>Full Motion Text *</label><textarea style={{ ...inp, height: '70px' }} value={resolutionForm.motion} onChange={e => setResolutionForm({ ...resolutionForm, motion: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Voting Threshold</label>
                <select style={inp} value={resolutionForm.voting_threshold} onChange={e => setResolutionForm({ ...resolutionForm, voting_threshold: e.target.value })}>
                  <option value="simple_majority">Simple Majority</option>
                  <option value="two_thirds">Two Thirds</option>
                  <option value="unanimous">Unanimous</option>
                </select>
              </div>
              <div style={fld()}><label style={lbl}>Outcome</label>
                <select style={inp} value={resolutionForm.outcome} onChange={e => setResolutionForm({ ...resolutionForm, outcome: e.target.value })}>
                  {['passed','rejected','deferred','withdrawn'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={fld()}><label style={lbl}>Votes For</label><input type="number" style={inp} value={resolutionForm.votes_for} onChange={e => setResolutionForm({ ...resolutionForm, votes_for: e.target.value })} /></div>
              <div style={fld()}><label style={lbl}>Votes Against</label><input type="number" style={inp} value={resolutionForm.votes_against} onChange={e => setResolutionForm({ ...resolutionForm, votes_against: e.target.value })} /></div>
              <div style={fld()}><label style={lbl}>Abstentions</label><input type="number" style={inp} value={resolutionForm.votes_abstained} onChange={e => setResolutionForm({ ...resolutionForm, votes_abstained: e.target.value })} /></div>
              <div style={fld()}><label style={lbl}>Implementation Deadline</label><input type="date" style={inp} value={resolutionForm.implementation_deadline} onChange={e => setResolutionForm({ ...resolutionForm, implementation_deadline: e.target.value })} /></div>
              <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={lbl}>Supporting Document (PDF) *</label>
                <input type='file' accept='.pdf' onChange={e => setResolutionFile(e.target.files[0])} style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} required />
                {resolutionFile && <span style={{ fontSize: '11px', color: '#27ae60' }}>✓ {resolutionFile.name} ({(resolutionFile.size/1024).toFixed(0)} KB)</span>}
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('#2c3e50')}>Save Resolution</button>
                <button type="button" onClick={() => setShowForm(false)} style={btn('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
              {['Number', 'Title', 'Meeting', 'Outcome', 'Votes', 'Impl. Status', 'Deadline', 'Document', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {resolutions.length === 0 && <tr><td colSpan="9" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No resolutions yet</td></tr>}
              {resolutions.map((r, i) => (
                editingId === `resolution-${r.id}` ? (
                  <tr key={r.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                    <td style={td}><input value={editData.resolution_number || ''} onChange={e => setEditData({ ...editData, resolution_number: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input value={editData.title || ''} onChange={e => setEditData({ ...editData, title: e.target.value })} style={inlineInp} /></td>
                    <td style={td}>{r.meeting_title}</td>
                    <td style={td}><select value={editData.outcome || ''} onChange={e => setEditData({ ...editData, outcome: e.target.value })} style={inlineInp}>{['passed','rejected','deferred','withdrawn'].map(o => <option key={o} value={o}>{o}</option>)}</select></td>
                    <td style={td}><input type="number" value={editData.votes_for || ''} onChange={e => setEditData({ ...editData, votes_for: e.target.value })} style={{ ...inlineInp, width: '50px' }} /></td>
                    <td style={td}><select value={editData.implementation_status || ''} onChange={e => setEditData({ ...editData, implementation_status: e.target.value })} style={inlineInp}>{IMPL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td style={td}><input type="date" value={editData.implementation_deadline || ''} onChange={e => setEditData({ ...editData, implementation_deadline: e.target.value })} style={inlineInp} /></td>
                    <td style={{ ...td, fontSize: '11px', color: '#666' }}>—</td>
                    <td style={td}>
                      {!readOnly && <button onClick={() => saveEdit('resolutions', r.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                      <button onClick={cancelEdit} style={smBtn('#95a5a6')}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={td}>{r.resolution_number}</td>
                    <td style={td}>{r.title}</td>
                    <td style={{ ...td, fontSize: '11px', color: '#666' }}>{r.meeting_title}</td>
                    <td style={td}>{badge(outcomeColor[r.outcome] || '#95a5a6', r.outcome)}</td>
                    <td style={{ ...td, fontSize: '12px' }}>{r.votes_for}F / {r.votes_against}A / {r.votes_abstained}Ab</td>
                    <td style={td}>{badge(implColor[r.implementation_status] || '#95a5a6', r.implementation_status || 'pending')}</td>
                    <td style={td}>{r.implementation_deadline?.slice(0, 10) || '—'}</td>
                    <td style={td}>{r.filename ? <a href={(process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/uploads/resolutions/' + r.filename} target='_blank' rel='noreferrer' style={{ color: '#2980b9', fontSize: '11px' }}>📄 View</a> : '—'}</td>
                    <td style={td}>{!readOnly && <button onClick={() => { setEditingId(`resolution-${r.id}`); setEditData({ resolution_number: r.resolution_number, title: r.title, outcome: r.outcome, votes_for: r.votes_for, votes_against: r.votes_against, votes_abstained: r.votes_abstained, implementation_status: r.implementation_status || 'pending', implementation_deadline: r.implementation_deadline?.slice(0, 10) || '' }); }} style={smBtn('#e67e22')}>Edit</button>}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── LEADERSHIP ── */}
      {activeTab === 'leadership' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setLeadershipView('active')} style={{
                padding: '7px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
                background: leadershipView === 'active' ? '#2c3e50' : '#ecf0f1',
                color: leadershipView === 'active' ? 'white' : '#7f8c8d'
              }}>Active ({leadership.filter(l => l.status === 'active').length})</button>
              <button onClick={() => setLeadershipView('retired')} style={{
                padding: '7px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
                background: leadershipView === 'retired' ? '#8e44ad' : '#ecf0f1',
                color: leadershipView === 'retired' ? 'white' : '#7f8c8d'
              }}>🕊 Retired Leaders ({leadership.filter(l => l.status === 'retired').length})</button>
            </div>
            <button onClick={downloadLeadershipPDF} disabled={leadershipPdfGenerating} style={{
              background: leadershipPdfGenerating ? '#95a5a6' : '#c0392b', color: 'white', border: 'none',
              padding: '8px 16px', borderRadius: '6px', cursor: leadershipPdfGenerating ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: 'bold'
            }}>
              {leadershipPdfGenerating ? 'Generating...' : '📄 Download Register PDF'}
            </button>
          </div>
          {!readOnly && showForm && (
            <form onSubmit={submitLeadership} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ width: '100%', margin: '0 0 8px', color: '#2c3e50' }}>Add Leadership Record</h3>
              <div style={fld()}><label style={lbl}>Member *</label>
                <select style={inp} value={leadershipForm.member_id} onChange={e => setLeadershipForm({ ...leadershipForm, member_id: e.target.value })} required>
                  <option value="">-- Select --</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>)}
                </select>
              </div>
              <div style={fld()}><label style={lbl}>Position *</label><input style={inp} value={leadershipForm.position} onChange={e => setLeadershipForm({ ...leadershipForm, position: e.target.value })} placeholder="e.g. Chairperson" required /></div>
              <div style={fld()}><label style={lbl}>Committee</label>
                <select style={inp} value={leadershipForm.committee} onChange={e => setLeadershipForm({ ...leadershipForm, committee: e.target.value })}>
                  {COMMITTEES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={fld()}><label style={lbl}>Election Date *</label><input type="date" style={inp} value={leadershipForm.election_date} onChange={e => setLeadershipForm({ ...leadershipForm, election_date: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Term Start *</label><input type="date" style={inp} value={leadershipForm.term_start} onChange={e => setLeadershipForm({ ...leadershipForm, term_start: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Term End</label><input type="date" style={inp} value={leadershipForm.term_end} onChange={e => setLeadershipForm({ ...leadershipForm, term_end: e.target.value })} /></div>
              <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('#2c3e50')}>Save Record</button>
                <button type="button" onClick={() => setShowForm(false)} style={btn('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
              {['Name', 'Position', 'Committee', 'Term Start', 'Term End', 'Status', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {leadership.filter(l => l.status === leadershipView).length === 0 && (
                <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>
                  {leadershipView === 'active' ? 'No active leadership records' : 'No retired leaders yet'}
                </td></tr>
              )}
              {leadership.filter(l => l.status === leadershipView).map((l, i) => (
                editingId === `leadership-${l.id}` ? (
                  <tr key={l.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                    <td style={td}><strong>{l.full_name}</strong></td>
                    <td style={td}><input value={editData.position || ''} onChange={e => setEditData({ ...editData, position: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><select value={editData.committee || ''} onChange={e => setEditData({ ...editData, committee: e.target.value })} style={inlineInp}>{COMMITTEES.map(c => <option key={c} value={c}>{c}</option>)}</select></td>
                    <td style={td}><input type="date" value={editData.term_start || ''} onChange={e => setEditData({ ...editData, term_start: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input type="date" value={editData.term_end || ''} onChange={e => setEditData({ ...editData, term_end: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><select value={editData.status || ''} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInp}><option value="active">Active</option><option value="retired">Retired</option><option value="suspended">Suspended</option></select></td>
                    <td style={td}>
                      {!readOnly && <button onClick={() => saveEdit('leadership', l.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                      <button onClick={cancelEdit} style={smBtn('#95a5a6')}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={td}><strong>{l.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{l.member_number}</span></td>
                    <td style={td}>{l.position}</td>
                    <td style={{ ...td, fontSize: '11px' }}>{l.committee}</td>
                    <td style={td}>{l.term_start?.slice(0, 10)}</td>
                    <td style={td}>{l.term_end?.slice(0, 10) || 'Current'}</td>
                    <td style={td}>{badge(l.status === 'active' ? '#27ae60' : l.status === 'retired' ? '#8e44ad' : '#95a5a6', l.status)}</td>
                    <td style={td}>
                      {!readOnly && <button onClick={() => { setEditingId(`leadership-${l.id}`); setEditData({ position: l.position, committee: l.committee, term_start: l.term_start?.slice(0, 10) || '', term_end: l.term_end?.slice(0, 10) || '', status: l.status }); }} style={{ ...smBtn('#e67e22'), marginRight: '4px' }}>Edit</button>}
                      {!readOnly && l.status === 'active' && (
                        <button onClick={() => retireLeader(l)} style={smBtn('#8e44ad')}>Retire</button>
                      )}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── CORRESPONDENCE ── */}
      {activeTab === 'correspondence' && (
        <div>
          {!readOnly && showForm && (
            <form onSubmit={submitCorr} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ width: '100%', margin: '0 0 8px', color: '#2c3e50' }}>Log Correspondence</h3>
              <div style={fld()}><label style={lbl}>Reference No. *</label><input style={inp} value={corrForm.ref_number} onChange={e => setCorrForm({ ...corrForm, ref_number: e.target.value })} placeholder="CORR-2026-001" required /></div>
              <div style={fld()}><label style={lbl}>Direction</label>
                <select style={inp} value={corrForm.direction} onChange={e => setCorrForm({ ...corrForm, direction: e.target.value })}>
                  <option value="IN">Received (IN)</option><option value="OUT">Sent (OUT)</option>
                </select>
              </div>
              <div style={{ ...fld(2) }}><label style={lbl}>Correspondent *</label><input style={inp} value={corrForm.correspondent} onChange={e => setCorrForm({ ...corrForm, correspondent: e.target.value })} required /></div>
              <div style={{ ...fld(2) }}><label style={lbl}>Subject *</label><input style={inp} value={corrForm.subject} onChange={e => setCorrForm({ ...corrForm, subject: e.target.value })} required /></div>
              <div style={fld()}><label style={lbl}>Category</label>
                <select style={inp} value={corrForm.category} onChange={e => setCorrForm({ ...corrForm, category: e.target.value })}>
                  {['general','legal','financial','administrative','disciplinary'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={fld()}><label style={lbl}>Date *</label><input type="date" style={inp} value={corrForm.date} onChange={e => setCorrForm({ ...corrForm, date: e.target.value })} required /></div>
              <div style={{ width: '100%' }}><label style={lbl}>Summary</label><textarea style={{ ...inp, height: '60px' }} value={corrForm.summary} onChange={e => setCorrForm({ ...corrForm, summary: e.target.value })} /></div>
              <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={lbl}>Document (PDF) *</label>
                <input type='file' accept='.pdf' onChange={e => setCorrFile(e.target.files[0])} style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} required />
                {corrFile && <span style={{ fontSize: '11px', color: '#27ae60' }}>✓ {corrFile.name} ({(corrFile.size/1024).toFixed(0)} KB)</span>}
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('#2c3e50')}>Log Correspondence</button>
                <button type="button" onClick={() => setShowForm(false)} style={btn('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
              {['Reference', 'Direction', 'Correspondent', 'Subject', 'Category', 'Date', 'Document', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {correspondence.length === 0 && <tr><td colSpan="8" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No correspondence logged</td></tr>}
              {correspondence.map((c, i) => (
                editingId === `corr-${c.id}` ? (
                  <tr key={c.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                    <td style={td}><input value={editData.ref_number || ''} onChange={e => setEditData({ ...editData, ref_number: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><select value={editData.direction || ''} onChange={e => setEditData({ ...editData, direction: e.target.value })} style={inlineInp}><option value="IN">IN</option><option value="OUT">OUT</option></select></td>
                    <td style={td}><input value={editData.correspondent || ''} onChange={e => setEditData({ ...editData, correspondent: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><input value={editData.subject || ''} onChange={e => setEditData({ ...editData, subject: e.target.value })} style={inlineInp} /></td>
                    <td style={td}><select value={editData.category || ''} onChange={e => setEditData({ ...editData, category: e.target.value })} style={inlineInp}>{['general','legal','financial','administrative','disciplinary'].map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></td>
                    <td style={td}><input type="date" value={editData.date || ''} onChange={e => setEditData({ ...editData, date: e.target.value })} style={inlineInp} /></td>
                    <td style={{ ...td, fontSize: '11px', color: '#666' }}>—</td>
                    <td style={td}>
                      {!readOnly && <button onClick={() => saveEdit('correspondence', c.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                      <button onClick={cancelEdit} style={smBtn('#95a5a6')}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '12px' }}>{c.ref_number}</td>
                    <td style={td}>{badge(dirColor[c.direction] || '#95a5a6', c.direction)}</td>
                    <td style={td}>{c.correspondent}</td>
                    <td style={td}>{c.subject}</td>
                    <td style={{ ...td, fontSize: '11px', color: '#666' }}>{c.category}</td>
                    <td style={td}>{c.date?.slice(0, 10)}</td>
                    <td style={td}>{c.filename ? <a href={(process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/uploads/correspondence/' + c.filename} target='_blank' rel='noreferrer' style={{ color: '#2980b9', fontSize: '11px' }}>📄 View</a> : '—'}</td>
                    <td style={td}>{!readOnly && <button onClick={() => { setEditingId(`corr-${c.id}`); setEditData({ ref_number: c.ref_number, direction: c.direction, correspondent: c.correspondent, subject: c.subject, category: c.category, date: c.date?.slice(0, 10) || '' }); }} style={smBtn('#e67e22')}>Edit</button>}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MINUTES ── */}
      {activeTab === 'minutes' && (
        <div>
          {!readOnly && showForm && (
            <form onSubmit={uploadMinutes} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ width: '100%', margin: '0 0 8px', color: '#2c3e50' }}>Upload Meeting Minutes</h3>
              <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Meeting *</label>
                <select style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} value={uploadForm.meeting_id} onChange={e => setUploadForm({ ...uploadForm, meeting_id: e.target.value })} required>
                  <option value=''>-- Select Meeting --</option>
                  {meetings.map(m => <option key={m.id} value={m.id}>{m.meeting_number} — {m.title} ({m.date?.slice(0,10)})</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>PDF File *</label>
                <input type='file' accept='.pdf' onChange={e => setSelectedFile(e.target.files[0])} style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} required />
                {selectedFile && <span style={{ fontSize: '11px', color: '#27ae60' }}>✓ {selectedFile.name} ({(selectedFile.size/1024).toFixed(0)} KB)</span>}
              </div>
              <div style={{ flex: 2, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Notes (optional)</label>
                <input style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} value={uploadForm.notes} onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })} placeholder='e.g. Final approved minutes' />
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                <button type='submit' disabled={uploading} style={{ background: uploading ? '#95a5a6' : '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>{uploading ? 'Uploading...' : 'Upload Minutes'}</button>
                <button type='button' onClick={() => setShowForm(false)} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              </div>
            </form>
          )}
          {minutes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>No minutes uploaded yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
                {['Meeting', 'Date', 'Title', 'File', 'Size', 'Uploaded', 'Notes', 'Actions'].map(h => <th key={h} style={{ padding: '10px', textAlign: 'left', fontSize: '12px' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {minutes.map((m, i) => (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontFamily: 'monospace', fontSize: '12px' }}>{m.meeting_number}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>{m.meeting_date?.slice(0,10)}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>{m.meeting_title}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', color: '#e74c3c', fontSize: '12px' }}>📄 {m.original_name}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '12px', color: '#666' }}>{m.file_size ? (m.file_size/1024).toFixed(0) + ' KB' : '—'}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '12px', color: '#666' }}>{m.uploaded_at ? new Date(m.uploaded_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '12px', color: '#666' }}>{m.notes || '—'}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <a href={(process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/uploads/minutes/' + m.filename} target='_blank' rel='noreferrer' style={{ background: '#2980b9', color: 'white', padding: '4px 10px', borderRadius: '4px', fontSize: '11px', textDecoration: 'none' }}>Download</a>
                        {!readOnly && <button onClick={() => deleteMinutes(m.id)} style={{ background: '#fde8e8', color: '#c0392b', border: '1px solid #f5c6cb', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── MEMBER BIO ── */}
      {activeTab === 'bio' && (
        <div>
          <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '8px', padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <label style={{ ...lbl, color: '#1a5276', display: 'block', marginBottom: '6px' }}>Select Member to View / Edit BIO</label>
              <select style={inp} value={bioMemberId} onChange={e => { setBioMemberId(e.target.value); loadBio(e.target.value); }}>
                <option value=''>-- Select Member --</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.full_name} — {m.member_number}</option>)}
              </select>
            </div>
            {bioData && <div style={{ fontSize: '12px', color: '#1a5276', padding: '8px 12px', background: '#d6eaf8', borderRadius: '6px' }}>✓ BIO on file — last updated {bioData.updated_at?.slice(0, 10)}</div>}
            {bioMemberId && !bioData && !bioLoading && <div style={{ fontSize: '12px', color: '#e67e22', padding: '8px 12px', background: '#fef5e7', borderRadius: '6px' }}>No BIO on file yet</div>}
            {bioMemberId && bioData && (
              <button onClick={downloadBioPDF} disabled={pdfGenerating} style={{ background: pdfGenerating ? '#95a5a6' : '#c0392b', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: pdfGenerating ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                {pdfGenerating ? 'Generating...' : '📄 Download PDF'}
              </button>
            )}
          </div>
          {bioLoading && <div style={{ textAlign: 'center', padding: '30px', color: '#95a5a6' }}>Loading...</div>}
          {bioMemberId && !bioLoading && (
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 2, minWidth: '320px' }}>
                <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '18px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '14px', fontSize: '14px' }}>🏠 Address / Identification</div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <label style={lbl}>LC1</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.lc1} onChange={e => setBioForm({ ...bioForm, lc1: e.target.value })} placeholder="LC1" />
                    </div>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <label style={lbl}>LC2</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.lc2} onChange={e => setBioForm({ ...bioForm, lc2: e.target.value })} placeholder="LC2" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <label style={lbl}>LC3</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.lc3} onChange={e => setBioForm({ ...bioForm, lc3: e.target.value })} placeholder="LC3" />
                    </div>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <label style={lbl}>District</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.district} onChange={e => setBioForm({ ...bioForm, district: e.target.value })} placeholder="District" />
                    </div>
                  </div>
                </div>
                <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '18px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '14px', fontSize: '14px' }}>👨‍👩‍👧 Parents</div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={lbl}>Father's Name</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.father_name} onChange={e => setBioForm({ ...bioForm, father_name: e.target.value })} placeholder="Full name" />
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={lbl}>Mother's Name</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.mother_name} onChange={e => setBioForm({ ...bioForm, mother_name: e.target.value })} placeholder="Full name" />
                    </div>
                  </div>
                </div>
                <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '18px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '14px', fontSize: '14px' }}>💍 Spouse{bioSpouses.length !== 1 ? 's' : ''} ({bioSpouses.length})</div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <input style={{ ...inp, flex: 1 }} value={newSpouseName} onChange={e => setNewSpouseName(e.target.value)} placeholder="Spouse full name" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), !readOnly && addSpouse())} />
                    {!readOnly && <button onClick={addSpouse} style={btn('#27ae60')}>+ Add Spouse</button>}
                  </div>
                  {bioSpouses.length === 0
                    ? <div style={{ color: '#95a5a6', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>No spouse recorded</div>
                    : bioSpouses.map((s, i) => (
                        editingSpouseId === s.id ? (
                          <div key={s.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '6px 8px', background: '#fffde7', borderRadius: '4px', marginBottom: '4px' }}>
                            <input style={{ ...inp, flex: 1, padding: '5px 8px' }} value={editingSpouseName} onChange={e => setEditingSpouseName(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), !readOnly && saveSpouseEdit(s.id))} autoFocus />
                            {!readOnly && <button onClick={() => saveSpouseEdit(s.id)} style={{ background: '#27ae60', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Save</button>}
                            <button onClick={() => setEditingSpouseId(null)} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                          </div>
                        ) : (
                          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: i % 2 === 0 ? '#f9f9f9' : 'white', borderRadius: '4px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', color: '#2c3e50' }}>💍 {s.spouse_name}</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {!readOnly && <button onClick={() => { setEditingSpouseId(s.id); setEditingSpouseName(s.spouse_name); }} style={{ background: '#eaf4fb', color: '#1a5276', border: '1px solid #aed6f1', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Edit</button>}
                              {!readOnly && <button onClick={() => removeSpouse(s.id)} style={{ background: '#fde8e8', color: '#c0392b', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Remove</button>}
                            </div>
                          </div>
                        )
                      ))}
                </div>
                <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '18px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '14px', fontSize: '14px' }}>🆘 Next of Kin</div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 2, minWidth: '200px' }}>
                      <label style={lbl}>Full Name</label>
                      <input style={{ ...inp, marginTop: '4px' }} value={bioForm.next_of_kin_name} onChange={e => setBioForm({ ...bioForm, next_of_kin_name: e.target.value })} placeholder="Next of kin full name" />
                    </div>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={lbl}>Relationship</label>
                      <select style={{ ...inp, marginTop: '4px' }} value={bioForm.next_of_kin_relationship} onChange={e => setBioForm({ ...bioForm, next_of_kin_relationship: e.target.value })}>
                        <option value=''>-- Select --</option>
                        {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                {!readOnly && <button onClick={saveBio} disabled={bioSaving} style={{ background: bioSaving ? '#95a5a6' : '#1a5276', color: 'white', border: 'none', padding: '10px 28px', borderRadius: '6px', cursor: bioSaving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                  {bioSaving ? 'Saving...' : '💾 Save BIO Data'}
                </button>}
                {!readOnly && bioData && (
                  <button onClick={() => { setDeleteBioModal(true); setDeleteBioConfirmText(''); }} style={{ marginLeft: '10px', background: 'white', color: '#c0392b', border: '1px solid #c0392b', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                    🗑 Delete BIO Record
                  </button>
                )}
              </div>
              <div style={{ flex: 1, minWidth: '260px' }}>
                <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '18px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '14px', fontSize: '14px' }}>👶 Children ({bioChildren.length})</div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <input style={{ ...inp, flex: 1 }} value={newChildName} onChange={e => setNewChildName(e.target.value)} placeholder="Child's full name" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), !readOnly && addChild())} />
                    {!readOnly && <button onClick={addChild} style={btn('#27ae60')}>Add</button>}
                  </div>
                  {bioChildren.length === 0
                    ? <div style={{ color: '#95a5a6', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No children recorded</div>
                    : bioChildren.map((c, i) => (
                        editingChildId === c.id ? (
                          <div key={c.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '6px 8px', background: '#fffde7', borderRadius: '4px', marginBottom: '4px' }}>
                            <input style={{ ...inp, flex: 1, padding: '5px 8px' }} value={editingChildName} onChange={e => setEditingChildName(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), !readOnly && saveChildEdit(c.id))} autoFocus />
                            {!readOnly && <button onClick={() => saveChildEdit(c.id)} style={{ background: '#27ae60', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Save</button>}
                            <button onClick={() => setEditingChildId(null)} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                          </div>
                        ) : (
                          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: i % 2 === 0 ? '#f9f9f9' : 'white', borderRadius: '4px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', color: '#2c3e50' }}>👤 {c.child_name}</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {!readOnly && <button onClick={() => { setEditingChildId(c.id); setEditingChildName(c.child_name); }} style={{ background: '#eaf4fb', color: '#1a5276', border: '1px solid #aed6f1', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Edit</button>}
                              {!readOnly && <button onClick={() => removeChild(c.id)} style={{ background: '#fde8e8', color: '#c0392b', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Remove</button>}
                            </div>
                          </div>
                        )
                      ))}
                </div>
                {(bioForm.father_name || bioForm.mother_name || bioSpouses.length > 0 || bioForm.next_of_kin_name) && (
                  <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '8px', padding: '14px 16px', marginTop: '16px', fontSize: '13px' }}>
                    <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '8px' }}>BIO Summary</div>
                    {bioForm.father_name && <div style={{ marginBottom: '4px' }}>👨 Father: {bioForm.father_name}</div>}
                    {bioForm.mother_name && <div style={{ marginBottom: '4px' }}>👩 Mother: {bioForm.mother_name}</div>}
                    {bioSpouses.length > 0 && <div style={{ marginBottom: '4px' }}>💍 Spouse{bioSpouses.length !== 1 ? 's' : ''}: {bioSpouses.map(s => s.spouse_name).join(', ')}</div>}
                    {bioForm.next_of_kin_name && <div style={{ marginBottom: '4px' }}>🆘 Next of Kin: {bioForm.next_of_kin_name} ({bioForm.next_of_kin_relationship || '—'})</div>}
                    {bioChildren.length > 0 && <div>👶 Children: {bioChildren.length}</div>}
                  </div>
                )}
              </div>
            </div>
          )}
          {!bioMemberId && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#95a5a6' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
              <div style={{ fontSize: '16px' }}>Select a member above to view or enter their BIO data</div>
            </div>
          )}
        </div>
      )}

      {/* ── MEMORIAL REGISTER ── */}
      {activeTab === 'memorial' && (
        <div>
          {/* KPI row */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {[
              { label: 'Total Records',        val: memorial.length,                                                     color: '#6c3483' },
              { label: 'Pending Package',       val: memorial.filter(b => b.status === 'reported' || b.status === 'approved').length, color: '#f39c12' },
              { label: 'Packages Paid',         val: memorial.filter(b => b.status === 'paid').length,                   color: '#27ae60' },
              { label: 'Total Amount Disbursed',val: `UGX ${memorial.reduce((s,b) => s + parseFloat(b.amount_paid||0), 0).toLocaleString()}`, color: '#2c3e50' },
            ].map((k, i) => (
              <div key={i} style={{ background: k.color, color: 'white', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '140px' }}>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>{k.label}</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '2px' }}>{k.val}</div>
              </div>
            ))}
          </div>

          {/* Add form */}
          {!readOnly && showForm && (
            <form onSubmit={submitMemorial} style={{ background: '#f9f0ff', border: '1px solid #d2b4de', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ width: '100%', margin: '0 0 8px', color: '#6c3483' }}>🕊 Add Memorial Record</h3>

              <div style={fld()}><label style={lbl}>Member *</label>
                <select style={inp} value={memorialForm.member_id} onChange={e => setMemorialForm({ ...memorialForm, member_id: e.target.value, deceased_name: '' })} required>
                  <option value="">-- Select Member --</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.full_name} — {m.member_number}</option>)}
                </select>
              </div>

              <div style={fld()}><label style={lbl}>Relationship (Who Passed) *</label>
                <select style={inp} value={memorialForm.relationship} onChange={e => setMemorialForm({ ...memorialForm, relationship: e.target.value, deceased_name: '' })}>
                  {MEMORIAL_RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div style={fld()}><label style={lbl}>Name of Deceased *</label>
                <select style={inp} value={memorialForm.deceased_name} onChange={e => setMemorialForm({ ...memorialForm, deceased_name: e.target.value })} required disabled={!memorialForm.member_id || memBioLoading}>
                  <option value="">{!memorialForm.member_id ? '-- Select member first --' : memBioLoading ? 'Loading BIO...' : memorialNames.length ? '-- Select name --' : `-- No ${memorialForm.relationship} on this member's BIO --`}</option>
                  {memorialNames.map(n => {
                    const rec = isAlreadyRecorded(n);
                    return <option key={n} value={n} disabled={rec}>{n}{rec ? ' (already recorded)' : ''}</option>;
                  })}
                </select>
                {memorialForm.member_id && !memBioLoading && memorialNames.length === 0 && (
                  <div style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px' }}>Fill this in on the member's BIO first (Member BIO tab).</div>
                )}
              </div>

              <div style={fld()}><label style={lbl}>Burial Date</label>
                <input type="date" style={inp} value={memorialForm.date_of_death} onChange={e => setMemorialForm({ ...memorialForm, date_of_death: e.target.value })} />
              </div>

              <div style={fld()}><label style={lbl}>Bereavement Package (UGX)</label>
                <input type="number" style={inp} value={memorialForm.package_amount} onChange={e => setMemorialForm({ ...memorialForm, package_amount: e.target.value })} placeholder="0" />
              </div>

              <div style={fld()}><label style={lbl}>Status</label>
                <select style={inp} value={memorialForm.status} onChange={e => setMemorialForm({ ...memorialForm, status: e.target.value })}>
                  {MEMORIAL_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>

              <div style={{ width: '100%' }}><label style={lbl}>Notes</label>
                <textarea style={{ ...inp, height: '50px' }} value={memorialForm.notes} onChange={e => setMemorialForm({ ...memorialForm, notes: e.target.value })} placeholder="Any additional notes" />
              </div>

              <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('#6c3483')}>Save Record</button>
                <button type="button" onClick={() => setShowForm(false)} style={btn('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}

          {/* Records table */}
          {memorial.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#95a5a6' }}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>🕊</div>
              <div>No memorial records yet</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#6c3483', color: 'white' }}>
                  <th style={th}>Member</th>
                  <th style={th}>Relationship</th>
                  <th style={th}>Name of Deceased</th>
                  <th style={th}>Burial Date</th>
                  <th style={{ ...th, textAlign: 'right' }}>Bereavement Package (UGX)</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {memorial.map((b, i) => (
                  editingId === `mem-${b.id}` ? (
                    <tr key={b.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={td}>{b.member_name || '—'}</td>
                      <td style={td}>
                        <select value={editData.relationship || ''} onChange={e => setEditData({ ...editData, relationship: e.target.value })} style={inlineInp}>
                          {MEMORIAL_RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td style={td}><input value={editData.deceased_name || ''} onChange={e => setEditData({ ...editData, deceased_name: e.target.value })} style={inlineInp} /></td>
                      <td style={td}><input type="date" value={editData.date_of_death || ''} onChange={e => setEditData({ ...editData, date_of_death: e.target.value })} style={inlineInp} /></td>
                      <td style={td}><input type="number" value={editData.package_amount || ''} onChange={e => setEditData({ ...editData, package_amount: e.target.value })} style={inlineInp} /></td>
                      <td style={td}>
                        <select value={editData.status || ''} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInp}>
                          {MEMORIAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        {!readOnly && <button onClick={() => saveEdit('bereavement', b.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                        <button onClick={cancelEdit} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={b.id} style={{ background: i % 2 === 0 ? '#fdf9ff' : 'white' }}>
                      <td style={td}>
                        <strong>{b.member_name || '—'}</strong>
                        {b.member_number && <div style={{ fontSize: '11px', color: '#95a5a6' }}>{b.member_number}</div>}
                      </td>
                      <td style={td}>
                        <span style={{ background: '#6c348320', color: '#6c3483', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                          {b.relationship}
                        </span>
                      </td>
                      <td style={{ ...td, fontWeight: 600 }}>{b.deceased_name}</td>
                      <td style={td}>{b.date_of_death ? b.date_of_death.slice(0, 10) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: parseFloat(b.package_amount||0) > 0 ? '#6c3483' : '#95a5a6' }}>
                        {parseFloat(b.package_amount || 0).toLocaleString()}
                      </td>
                      <td style={td}>
                        <span style={{ background: MEMORIAL_STATUS_COLORS[b.status] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                          {b.status?.toUpperCase()}
                        </span>
                      </td>
                      <td style={td}>
                        {!readOnly && <button onClick={() => {
                          setEditingId(`mem-${b.id}`);
                          setEditData({ deceased_name: b.deceased_name, relationship: b.relationship, date_of_death: b.date_of_death?.slice(0,10) || '', package_amount: b.package_amount, status: b.status });
                        }} style={{ ...smBtn('#e67e22'), marginRight: '4px' }}>Edit</button>}
                        {!readOnly && b.status !== 'paid' && (
                          <button onClick={() => openDisburseModal(b)} style={{ ...smBtn('#27ae60'), marginRight: '4px' }}>Disburse</button>
                        )}
                        {!readOnly && <button onClick={() => deleteMemorial(b)} style={{ background: '#fde8e8', color: '#c0392b', border: '1px solid #f5c6cb', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── DELETE BIO RECORD MODAL ── */}
      {deleteBioModal && currentBioMember && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '440px', maxWidth: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#c0392b', marginBottom: '12px' }}>🗑 Delete Entire BIO Record</div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '18px', lineHeight: 1.5 }}>
              This permanently deletes <strong>{currentBioMember.full_name}</strong>'s entire BIO record — parents, spouse(s), children, and next of kin. This does not affect the Memorial Register. This cannot be undone.
            </div>
            <label style={lbl}>Type the member's full name to confirm: <strong>{currentBioMember.full_name}</strong></label>
            <input style={{ ...inp, marginTop: '4px', marginBottom: '20px' }} value={deleteBioConfirmText} onChange={e => setDeleteBioConfirmText(e.target.value)} placeholder={currentBioMember.full_name} />
            <div style={{ display: 'flex', gap: '10px' }}>
              {!readOnly && <button onClick={deleteEntireBio} disabled={deletingBio} style={{ background: deletingBio ? '#95a5a6' : '#c0392b', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: deletingBio ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 'bold', flex: 1 }}>
                {deletingBio ? 'Deleting...' : 'Permanently Delete'}
              </button>}
              <button onClick={() => { setDeleteBioModal(false); setDeleteBioConfirmText(''); }} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DISBURSE MODAL ── */}
      {disburseModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '420px', maxWidth: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#27ae60', marginBottom: '12px' }}>💰 Disburse Bereavement Package</div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '18px', lineHeight: 1.5 }}>
              Disburse <strong>UGX {parseFloat(disburseModal.record.package_amount || 0).toLocaleString()}</strong> for <strong>{disburseModal.record.deceased_name}</strong> ({disburseModal.record.relationship}).
              This will debit the selected treasury account and mark the record as Paid.
            </div>
            <label style={lbl}>Debit From Account *</label>
            <select style={{ ...inp, marginTop: '4px', marginBottom: '20px' }} value={disburseAccountId} onChange={e => setDisburseAccountId(e.target.value)} required>
              <option value="">-- Select Account --</option>
              {treasuryAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name} — UGX {parseFloat(a.balance).toLocaleString()}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '10px' }}>
              {!readOnly && <button onClick={confirmDisburse} disabled={disbursing} style={{ background: disbursing ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: disbursing ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 'bold', flex: 1 }}>
                {disbursing ? 'Disbursing...' : 'Confirm Disbursement'}
              </button>}
              <button onClick={cancelDisburseModal} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Secretariat;